export type NamedShortcutKey =
  | 'mod'
  | 'ctrl'
  | 'shift'
  | 'alt'
  | 'enter'
  | 'esc'
  | 'space'
  | 'tab'
  | 'up'
  | 'down'
  | 'left'
  | 'right';

export type ShortcutPiece =
  | NamedShortcutKey
  | { char: string }
  | { example: string }
  | { sep: 'or' | '/' };

export type ShortcutRow = {
  pieces?: ShortcutPiece[];
  shortcut?: string;
  label: string;
};

export type ShortcutSection = {
  title?: string;
  rows: ShortcutRow[];
};

const char = (value: string): ShortcutPiece => ({ char: value });
const example = (value: string): ShortcutPiece => ({ example: value });
const sep = (value: 'or' | '/'): ShortcutPiece => ({ sep: value });

const markdownToolbarShortcutRows = (): ShortcutRow[] => [
  { pieces: ['mod', char('B')], label: 'Bold' },
  { pieces: ['mod', char('I')], label: 'Italic' },
  { pieces: ['mod', char('U')], label: 'Underline' },
  { pieces: ['mod', 'shift', char('S')], label: 'Strikethrough' },
  { pieces: ['mod', 'shift', char('H')], label: 'Highlight' },
  { pieces: ['mod', 'shift', char('B')], label: 'Block quote' },
  { pieces: ['mod', char('K')], label: 'Insert link' },
  { pieces: ['mod', char('E')], label: 'Inline code' },
  { pieces: ['mod', 'alt', char('C')], label: 'Code block' },
  { pieces: ['mod', 'alt', char('1')], label: 'Heading 1' },
  { pieces: ['mod', 'alt', char('2')], label: 'Heading 2' },
  { pieces: ['mod', 'alt', char('3')], label: 'Heading 3' },
  { pieces: ['mod', 'alt', char('4')], label: 'Heading 4' },
  { pieces: ['mod', 'alt', char('0')], label: 'Paragraph' },
  { pieces: ['mod', 'shift', char('8')], label: 'Bullet list' },
  { pieces: ['mod', 'shift', char('7')], label: 'Numbered list' },
  { pieces: ['mod', 'shift', char('9')], label: 'Checkbox list' }
];

const markdownSlashCommandRows: ShortcutRow[] = [
  { pieces: [char('/')], label: 'Open the command menu' },
  {
    pieces: [example('/John Smith')],
    label: 'Filter to a person/task by name, email, or task id'
  },
  {
    pieces: [example('/important')],
    label: 'Filter to matching commands such as severity or formatting'
  },
  { pieces: ['up', sep('/'), 'down'], label: 'Move through results' },
  { pieces: ['right'], label: 'Open a selected submenu' },
  { pieces: ['enter', sep('or'), 'tab'], label: 'Apply the selected command' },
  { pieces: ['left'], label: 'Go back to the previous menu level' },
  { pieces: ['esc'], label: 'Close the current menu' }
];

const markdownSections = (isMac: boolean): ShortcutSection[] => [
  {
    title: 'General',
    rows: [
      { pieces: ['mod', 'enter'], label: 'Submit task' },
      { pieces: ['esc'], label: 'Close feedback modal' },
      {
        pieces: ['mod', 'alt', 'shift', char('M')],
        label: 'Toggle Markdown source editing'
      },
      {
        pieces: isMac
          ? ['mod', 'ctrl', 'space']
          : [char('Win'), char('.')],
        label: 'Add emoji'
      }
    ]
  },
  {
    title: 'Toolbar formatting',
    rows: markdownToolbarShortcutRows()
  },
  {
    title: 'Slash command menu',
    rows: markdownSlashCommandRows
  }
];

export const getShortcutSections = ({
  isMac
}: {
  isMac: boolean;
}): ShortcutSection[] => markdownSections(isMac);
